/**
 * Geometria dos dados poliédricos.
 *
 * Princípio: a MESMA tabela de faces serve para renderizar e para ler o resultado.
 * Assim é impossível o dado mostrar 5 e o sistema registrar 3.
 *
 * As faces NÃO são escritas à mão. Definimos apenas os vértices de cada sólido e
 * derivamos as faces por casco convexo (teste de plano + verificação de que o
 * plano não corta o sólido). Isso elimina a classe de erro mais perigosa aqui:
 * assumir que as normais de face de um sólido são os vértices do seu dual "na
 * mesma orientação" — o que é falso para os conjuntos padrão de icosaedro e
 * dodecaedro.
 *
 * Ao final a construção é validada: número de faces e característica de Euler
 * (V - E + F = 2). Se algo estiver errado, falha alto em vez de gerar um dado
 * que mostra o número errado.
 */
import { type DieSides } from './expression.js';

/**
 * Dados com sólido geométrico. O d100 é exceção: não tem poliedro, é lido como
 * dezenas + unidades.
 */
export const POLYHEDRAL_SIDES = [4, 6, 8, 10, 12, 20] as const;
export type PolyhedralSides = (typeof POLYHEDRAL_SIDES)[number];

export type Vec3 = readonly [number, number, number];

export type DieFace = {
  value: number;
  /** normal externa, unitária, em espaço local */
  normal: Vec3;
  /** centro da face (onde o número é desenhado) */
  centroid: Vec3;
  /** polígono da face, em ordem, para renderização */
  polygon: Vec3[];
};

export type DieGeometry = {
  sides: DieSides;
  /** vértices normalizados, com o raio máximo igual a 1 */
  vertices: Vec3[];
  faces: DieFace[];
  radius: number;
  /**
   * Quando o dado para de rolar, a face do resultado é a que aponta para cima
   * — exceto no d4, que repousa sobre uma face e é lido pela face de baixo.
   */
  resultFaceIsUp: boolean;
};

const PHI = (1 + Math.sqrt(5)) / 2;
const INV_PHI = 1 / PHI;

/* --------------------------------------------------------------- vetores */

export function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function scale(a: Vec3, k: number): Vec3 {
  return [a[0] * k, a[1] * k, a[2] * k];
}

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

export function length(a: Vec3): number {
  return Math.sqrt(dot(a, a));
}

export function normalize(a: Vec3): Vec3 {
  const len = length(a);
  if (len === 0) throw new Error('vetor nulo não pode ser normalizado');
  return scale(a, 1 / len);
}

export function centroid(points: readonly Vec3[]): Vec3 {
  if (points.length === 0) throw new Error('centroide precisa de ao menos um ponto');
  let acc: Vec3 = [0, 0, 0];
  for (const point of points) acc = add(acc, point);
  return scale(acc, 1 / points.length);
}

/* --------------------------------------------------------------- vértices dos sólidos */

function signCombinations(): Vec3[] {
  const out: Vec3[] = [];
  for (const x of [-1, 1]) {
    for (const y of [-1, 1]) {
      for (const z of [-1, 1]) {
        out.push([x, y, z]);
      }
    }
  }
  return out;
}

function axisVertices(): Vec3[] {
  return [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1],
  ];
}

function icosahedronVertices(): Vec3[] {
  return [
    [0, 1, PHI],
    [0, 1, -PHI],
    [0, -1, PHI],
    [0, -1, -PHI],
    [1, PHI, 0],
    [1, -PHI, 0],
    [-1, PHI, 0],
    [-1, -PHI, 0],
    [PHI, 0, 1],
    [PHI, 0, -1],
    [-PHI, 0, 1],
    [-PHI, 0, -1],
  ];
}

function dodecahedronVertices(): Vec3[] {
  const out: Vec3[] = signCombinations();
  for (const a of [-INV_PHI, INV_PHI]) {
    for (const b of [-PHI, PHI]) {
      out.push([0, a, b]);
      out.push([a, b, 0]);
      out.push([b, 0, a]);
    }
  }
  return out;
}

/** Meio-ângulo entre vértices vizinhos do equador do trapezoedro (36°). */
const TRAPEZO_HALF_ANGLE = Math.PI / 5;

/**
 * Altura do zigue-zague do equador que torna as faces do trapezoedro PLANAS.
 *
 * Dedução: a face é (ápice, equador em +h, equador em -h, equador em +h). Exigindo
 * determinante nulo para os quatro pontos e chamando u = cos(36°), chega-se a
 * h = A (1 - u) / (1 + u). Com A = 1 isso dá h ≈ 0.105573.
 */
const TRAPEZO_APEX = 1;
const TRAPEZO_RADIUS = 1;
const TRAPEZO_ZIG =
  (TRAPEZO_APEX * (1 - Math.cos(TRAPEZO_HALF_ANGLE))) / (1 + Math.cos(TRAPEZO_HALF_ANGLE));

/**
 * Pentagonal trapezoedro (o d10): dois ápices e um equador em zigue-zague.
 * Não é sólido de Platão, então os vértices são construídos aqui.
 */
function trapezohedronVertices(): Vec3[] {
  const out: Vec3[] = [
    [0, TRAPEZO_APEX, 0],
    [0, -TRAPEZO_APEX, 0],
  ];
  for (let k = 0; k < 10; k += 1) {
    const theta = k * TRAPEZO_HALF_ANGLE;
    const y = k % 2 === 0 ? TRAPEZO_ZIG : -TRAPEZO_ZIG;
    out.push([TRAPEZO_RADIUS * Math.cos(theta), y, TRAPEZO_RADIUS * Math.sin(theta)]);
  }
  return out;
}

/** Somente os vértices: as faces vêm do casco convexo. */
export function solidVertices(sides: PolyhedralSides): Vec3[] {
  switch (sides) {
    case 4:
      return [
        [1, 1, 1],
        [-1, -1, 1],
        [-1, 1, -1],
        [1, -1, -1],
      ];
    case 6:
      return signCombinations();
    case 8:
      return axisVertices();
    case 10:
      return trapezohedronVertices();
    case 12:
      return dodecahedronVertices();
    case 20:
      return icosahedronVertices();
    default: {
      const exhaustive: never = sides;
      throw new Error(`vértices não definidos para d${String(exhaustive)}`);
    }
  }
}

/* --------------------------------------------------------------- casco convexo */

const PLANE_EPSILON = 1e-7;
const COPLANAR_EPSILON = 1e-6;

function orthonormalBasis(normal: Vec3): { u: Vec3; v: Vec3 } {
  const helper: Vec3 = Math.abs(normal[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const u = normalize(cross(normal, helper));
  const v = normalize(cross(normal, u));
  return { u, v };
}

/** Índices dos vértices que estão no plano de apoio da face, em ordem angular. */
function collectFaceIndices(vertices: readonly Vec3[], normal: Vec3): number[] {
  const distances = vertices.map((vertex) => dot(vertex, normal));
  const plane = Math.max(...distances);
  const indices = distances
    .map((distance, index) => ({ distance, index }))
    .filter(({ distance }) => Math.abs(distance - plane) <= COPLANAR_EPSILON)
    .map(({ index }) => index);

  if (indices.length < 3) return [];

  const points = indices.map((index) => vertices[index]!);
  const center = centroid(points);
  const { u, v } = orthonormalBasis(normal);

  return indices
    .slice()
    .sort((a, b) => {
      const da = sub(vertices[a]!, center);
      const db = sub(vertices[b]!, center);
      const angleA = Math.atan2(dot(da, v), dot(da, u));
      const angleB = Math.atan2(dot(db, v), dot(db, u));
      return angleA - angleB;
    });
}

type HullFace = { normal: Vec3; indices: number[] };

/** Enumera as faces de um poliedro convexo a partir dos seus vértices. */
export function hullFaces(vertices: readonly Vec3[]): HullFace[] {
  const count = vertices.length;
  const faces: HullFace[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < count; i += 1) {
    for (let j = i + 1; j < count; j += 1) {
      for (let k = j + 1; k < count; k += 1) {
        const a = vertices[i]!;
        const b = vertices[j]!;
        const c = vertices[k]!;

        let normal = cross(sub(b, a), sub(c, a));
        const magnitude = length(normal);
        if (magnitude < 1e-9) continue; // vértices colineares
        normal = scale(normal, 1 / magnitude);

        const plane = dot(normal, a);
        let above = 0;
        let below = 0;
        for (const vertex of vertices) {
          const distance = dot(normal, vertex) - plane;
          if (distance > PLANE_EPSILON) above += 1;
          else if (distance < -PLANE_EPSILON) below += 1;
        }

        // se há vértices dos dois lados, o plano corta o sólido: não é face
        if (above > 0 && below > 0) continue;
        // sólido inteiro acima do plano => a normal externa é a oposta
        if (below === 0) normal = scale(normal, -1);

        const indices = collectFaceIndices(vertices, normal);
        if (indices.length < 3) continue;

        const key = indices
          .slice()
          .sort((x, y) => x - y)
          .join(',');
        if (seen.has(key)) continue;
        seen.add(key);
        faces.push({ normal, indices });
      }
    }
  }

  return faces;
}

/* --------------------------------------------------------------- numeração das faces */

function compareVec3(a: Vec3, b: Vec3): number {
  if (a[1] !== b[1]) return b[1] - a[1];
  if (a[0] !== b[0]) return b[0] - a[0];
  return b[2] - a[2];
}

/**
 * Distribui os números 1..N entre as faces.
 * Em dados com número par de faces, faces opostas somam N+1 (como num dado real).
 */
function assignValues(normals: readonly Vec3[], sides: number): number[] {
  const total = normals.length;
  const opposite = normals.map((normal, index) => {
    let bestIndex = -1;
    let bestDot = -1;
    for (let j = 0; j < total; j += 1) {
      if (j === index) continue;
      const candidate = -dot(normal, normals[j]!);
      if (candidate > bestDot) {
        bestDot = candidate;
        bestIndex = j;
      }
    }
    return bestDot > 0.999 ? bestIndex : -1;
  });

  const order = normals
    .map((_, index) => index)
    .sort((a, b) => compareVec3(normals[a]!, normals[b]!));

  const values = new Array<number>(total).fill(0);
  let next = 1;

  for (const index of order) {
    if (values[index] !== 0) continue;
    const mirror = opposite[index] ?? -1;
    if (mirror >= 0 && values[mirror] === 0 && total % 2 === 0) {
      values[index] = next;
      values[mirror] = sides + 1 - next;
      next += 1;
    } else {
      values[index] = next;
      next += 1;
    }
  }

  const seen = new Set(values);
  if (seen.size !== total || values.some((value) => value < 1 || value > sides)) {
    throw new Error(`falha ao numerar as faces do d${sides}`);
  }
  return values;
}

/* --------------------------------------------------------------- validação */

function countEdges(faces: readonly HullFace[]): number {
  const edges = new Set<string>();
  for (const face of faces) {
    for (let i = 0; i < face.indices.length; i += 1) {
      const a = face.indices[i]!;
      const b = face.indices[(i + 1) % face.indices.length]!;
      edges.add(a < b ? `${a}-${b}` : `${b}-${a}`);
    }
  }
  return edges.size;
}

/* --------------------------------------------------------------- construção */

const CACHE = new Map<PolyhedralSides, DieGeometry>();

/** Constrói (e memoriza) a geometria de um dado. */
export function buildDieGeometry(sides: DieSides): DieGeometry {
  if (!hasSolidGeometry(sides)) {
    throw new Error(`d${sides}: geometria não definida (o d100 é lido como dezenas + unidades)`);
  }
  const cached = CACHE.get(sides);
  if (cached) return cached;

  const rawVertices = solidVertices(sides);
  const maxRadius = Math.max(...rawVertices.map(length));
  const vertices = rawVertices.map((vertex) => scale(vertex, 1 / maxRadius));

  const hull = hullFaces(vertices);

  if (hull.length !== sides) {
    throw new Error(`d${sides}: casco convexo gerou ${hull.length} faces (esperado ${sides})`);
  }

  const euler = vertices.length - countEdges(hull) + hull.length;
  if (euler !== 2) {
    throw new Error(`d${sides}: sólido inválido (V - E + F = ${euler}, esperado 2)`);
  }

  const values = assignValues(
    hull.map((face) => face.normal),
    sides,
  );

  const faces: DieFace[] = hull.map((face, index) => {
    const polygon = face.indices.map((vertexIndex) => vertices[vertexIndex]!);
    return {
      value: values[index]!,
      normal: face.normal,
      centroid: centroid(polygon),
      polygon,
    };
  });

  const geometry: DieGeometry = {
    sides,
    vertices,
    faces,
    radius: 1,
    resultFaceIsUp: sides !== 4,
  };

  CACHE.set(sides, geometry);
  return geometry;
}

/** Todas as geometrias poliédricas, na ordem de POLYHEDRAL_SIDES. */
export function buildAllDiceGeometries(): DieGeometry[] {
  return POLYHEDRAL_SIDES.map((sides) => buildDieGeometry(sides));
}

/** O d100 não tem poliedro: é lido como dezenas + unidades. */
export function hasSolidGeometry(sides: DieSides): sides is PolyhedralSides {
  return (POLYHEDRAL_SIDES as readonly number[]).includes(sides);
}
